import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LoggerService } from '../../../../../services/logger.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ColorPickerModule } from '@iplab/ngx-color-picker';
import { TextEditorComponent, TextEditorStyle } from '../../../../../shared/components/text-editor';
import { TemplateService } from '../../../../../car-wash-website/services/template.service';
import { NotificationService } from '../../../../../services/notification.service'; // NotificationService;

export interface TestimonialItem {
  id?: string;
  name: string;
  text: string;
  avatar: string;
  rating?: number;
}

export interface TestimonialsData {
  title: string;
  subtitle: string;
  titleStyle?: TextEditorStyle;
  testimonials: TestimonialItem[];
  cardStyle?: 'default' | 'bordered' | 'elevated';
  cardColor?: string;
  backgroundColor?: string;
}

/**
 * Minimal testimonials panel component for editing and configuring a small set of testimonial cards (up to 6), including text, avatars, ratings, card styles and background/card colors.
 * @component
 * @selector app-minimal-testimonials-panel
 * @example
 *   <app-minimal-testimonials-panel [data]="testimonialsData" (dataChange)="onDataChange($event)" (closePanel)="onClose()"></app-minimal-testimonials-panel>
 * @input TestimonialsData data - The editable testimonials configuration object. Includes title, subtitle, titleStyle (optional), testimonials[] (each with name, text, avatar, rating), cardStyle, cardColor and backgroundColor. The component ensures there are always six testimonial slots.
 * @output EventEmitter<TestimonialsData> dataChange - Emitted whenever the component updates the local data (field edits, style/color changes, avatar replace completes, etc.). Emits a shallow copy of the current data.
 * @output EventEmitter<void> closePanel - Emitted when the panel is requested to be closed (onClose).
 *
 * Purpose:
 * - Provides a minimal UI panel for modifying testimonials content and basic layout options (card style, background and card colors).
 * - Manages color pickers, custom/predefined color lists, avatar uploads with previews and server upload integration.
 *
 * Key methods:
 * - onHeadingChange(value), onSubtitleChange(value), onHeadingStyleChange(style): update heading/subtitle and style then emit dataChange.
 * - onTestimonialChange(index, field, value): update a specific testimonial field and emit dataChange.
 * - onCardStyleChange(style), onBackgroundColorChange(color), onCardColorChange(color): apply layout/color changes and emit dataChange.
 * - openColorPicker(color), closeColorPicker(), onColorPickerChange(): manage background color picker opening, preview, selection and adding a custom color.
 * - openCardColorPicker(color), closeCardColorPicker(), onCardColorPickerChange(): same as above for card colors.
 * - addCustomColor(color), addCardCustomColor(color), loadCustomColorFromData(), loadCardCustomColorFromData(): helpers to manage predefined and single custom color slots.
 * - onImageReplaceClick(index), onImageFileSelected(index, event), handleAvatarUpload(index, file): handle avatar file selection, validation (PNG/JPG/JPEG, max 5MB), temporary preview, and upload via TemplateService with success/error handling and emitting dataChange.
 * - toggleSection(section), isSectionExpanded(section), switchTab(tab): UI helpers for accordion-like sections and tab switching.
 * - getTestimonialLabel(index): returns a human-readable label for each testimonial slot.
 *
 * Lifecycle hooks:
 * - ngOnInit(): initializes the testimonials array to ensure six entries and loads any custom colors from input data.
 * - ngOnChanges(changes): watches for external changes to the input data and reloads custom/card colors when backgroundColor or cardColor changes.
 *
 * Important notes:
 * - Uses a predefined color palette and supports a single custom color per background/card (replaces existing custom color).
 * - Upload responses are parsed liberally for multiple possible URL shapes (string, {url}, {data:{url}}, {imageUrl}, {fileUrl}).
 * - Emits dataChange with a shallow-copied object to allow external consumers to react to changes.
 */
@Component({
  selector: 'app-minimal-testimonials-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, ColorPickerModule, TextEditorComponent],
  templateUrl: './testimonials-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./testimonials-panel.component.scss'],
})
export class MinimalTestimonialsPanelComponent implements OnInit, OnChanges {
  @Input() data: TestimonialsData = {
    title: '',
    subtitle: '',
    testimonials: [],
    cardStyle: 'default',
    cardColor: '#FFFFFF',
    backgroundColor: '#FFFFFF',
  };

  @Output() dataChange = new EventEmitter<TestimonialsData>();
  @Output() closePanel = new EventEmitter<void>();

  activeTab: 'content' | 'layout' = 'content';
  expandedSections: Record<string, boolean> = {
    heading: true,
    'testimonial-0': false,
    'testimonial-1': false,
    'testimonial-2': false,
    'testimonial-3': false,
    'testimonial-4': false,
    'testimonial-5': false,
    cardStyle: false,
    background: false,
  };

  customColors: string[] = [];
  cardCustomColors: string[] = [];

  // Color picker state (same as hero panel)
  isColorPickerOpen = false;
  currentPickerColor: string | any = '#000000';

  // Card color picker state
  isCardColorPickerOpen = false;
  currentCardPickerColor: string | any = '#000000';

  // Image upload state
  uploadingAvatars: Record<number, boolean> = {};
  uploadErrors: Record<number, string | null> = {};

  private static readonly PREDEFINED_COLORS = ['#5E19A1', '#FF9736', '#079271', '#1B5BFF'];

  constructor(
    private logger: LoggerService,
    private templateService: TemplateService,
    private messageService: NotificationService
  ) {}

  /**
   * Ensure the component has at least six testimonial slots (filling with empty placeholders if needed) and load any saved custom colors for the panel on initialization.
   * @example
   * gOnInit()
   * // returns void
   * @returns {void} No return value.
   */
  ngOnInit(): void {
    // Ensure we have at least 6 testimonial slots
    if (!this.data.testimonials) {
      this.data.testimonials = [];
    }

    // Fill up to 6 testimonials with empty ones if needed
    while (this.data.testimonials.length < 6) {
      this.data.testimonials.push({
        name: '',
        text: '',
        avatar: '',
        rating: 5,
      });
    }

    // Load custom color on initial load
    this.loadCustomColorFromData();
    this.loadCardCustomColorFromData();
  }

  // Lifecycle hook to detect when data input changes
  /**
   * Called when input-bound properties change; checks for 'data' changes and loads custom colors if present.
   * @example
   * gOnChanges({ data: { currentValue: { backgroundColor: '#ff0000', cardColor: '#00ff00' }, previousValue: null, firstChange: true } })
   * undefined
   * @param {{SimpleChanges}} {{changes}} - Map of changed input properties provided by Angular.
   * @returns {{void}} No return value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      if (this.data?.backgroundColor) {
        // Load custom color if backgroundColor is not in predefined colors
        this.loadCustomColorFromData();
      }
      if (this.data?.cardColor) {
        // Load custom card color if cardColor is not in predefined colors
        this.loadCardCustomColorFromData();
      }
    }
  }

  // Load custom color from data if it's not a predefined color
  /**
   * Loads a custom background color from the component's data and updates this.customColors accordingly.
   * @example
   * loadCustomColorFromData()
   * // If this.data.backgroundColor is not in PREDEFINED_COLORS, sets this.customColors = [thatColor]; otherwise sets this.customColors = [].
   * @returns {void} Updates this.customColors based on this.data.backgroundColor; no return value.
   */
  private loadCustomColorFromData(): void {
    if (!this.data?.backgroundColor) {
      return;
    }

    const bgColor = this.data.backgroundColor;

    // Check if the color is not in predefined colors
    if (!MinimalTestimonialsPanelComponent.PREDEFINED_COLORS.includes(bgColor)) {
      // Add to custom colors (maximum 1 custom color)
      this.customColors = [bgColor];
    } else {
      // If it's a predefined color, clear custom colors
      this.customColors = [];
    }
  }

  // Load custom card color from data if it's not a predefined color
  /**
   * Load a custom card color from the component's data and update component state accordingly.
   * @example
   * loadCardCustomColorFromData()
   * // Updates this.cardCustomColors to either [customColor] or [] based on this.data.cardColor
   * @returns {void} No return value.
   **/
  private loadCardCustomColorFromData(): void {
    if (!this.data?.cardColor) {
      return;
    }

    const cardColor = this.data.cardColor;

    // Check if the color is not in predefined colors
    if (!MinimalTestimonialsPanelComponent.PREDEFINED_COLORS.includes(cardColor)) {
      // Add to custom colors (maximum 1 custom color)
      this.cardCustomColors = [cardColor];
    } else {
      // If it's a predefined color, clear custom colors
      this.cardCustomColors = [];
    }
  }

  onClose(): void {
    this.closePanel.emit();
  }

  switchTab(tab: 'content' | 'layout'): void {
    this.activeTab = tab;
  }

  /**
   * Toggle the expansion state for the given section; if expanding, collapse all other sections first.
   * @example
   * toggleSection('testimonials')
   * undefined
   * @param {string} section - Name of the section to toggle.
   * @returns {void} No return value.
   **/
  toggleSection(section: string): void {
    const wasExpanded = this.expandedSections[section] || false;

    if (wasExpanded) {
      // If clicking an expanded section, just collapse it
      this.expandedSections[section] = false;
    } else {
      // If clicking a collapsed section, close all others first, then expand this one
      Object.keys(this.expandedSections).forEach(key => {
        this.expandedSections[key] = false;
      });
      this.expandedSections[section] = true;
    }
  }

  isSectionExpanded(section: string): boolean {
    return this.expandedSections[section] || false;
  }

  onHeadingChange(value: string): void {
    this.data.title = value;
    this.emitChange();
  }

  onHeadingStyleChange(style: TextEditorStyle): void {
    this.data.titleStyle = style;
    this.emitChange();
  }

  onSubtitleChange(value: string): void {
    this.data.subtitle = value;
    this.emitChange();
  }

  onTestimonialChange(
    index: number,
    field: 'name' | 'text' | 'avatar' | 'rating',
    value: any
  ): void {
    if (this.data.testimonials[index]) {
      (this.data.testimonials[index] as any)[field] = value;
      this.emitChange();
    }
  }

  onCardStyleChange(style: 'default' | 'bordered' | 'elevated'): void {
    this.data.cardStyle = style;
    this.emitChange();
  }

  onBackgroundColorChange(color: string): void {
    this.data.backgroundColor = color;
    this.emitChange();
  }

  onAddColor(): void {
    // Open color picker modal (same as hero panel)
    this.openColorPicker();
  }

  // Open color picker for custom color
  openColorPicker(color?: string): void {
    this.currentPickerColor = color || this.data.backgroundColor || '#000000';
    this.isColorPickerOpen = true;
  }

  // Close color picker and add the selected color to custom colors list
  /**
   * Close the color picker, apply the currently selected color (normalized to a hex string) as the background color, add it to custom colors (replacing any existing custom color), and mark the picker closed.
   * @example
   * loseColorPicker()
   * undefined
   * @returns {{void}} No value is returned; the method performs side effects on the component (applies color, updates custom colors, and closes the picker).
   **/
  closeColorPicker(): void {
    if (this.currentPickerColor) {
      // Extract hex color from color object (chrome-picker returns an object with hex property)
      let hexColor: string;
      if (typeof this.currentPickerColor === 'string') {
        hexColor = this.currentPickerColor;
      } else if (
        this.currentPickerColor &&
        typeof this.currentPickerColor === 'object' &&
        'hex' in this.currentPickerColor
      ) {
        hexColor = (this.currentPickerColor as any).hex;
      } else {
        hexColor = String(this.currentPickerColor);
      }

      // Ensure hex color starts with #
      if (!hexColor.startsWith('#')) {
        hexColor = '#' + hexColor;
      }

      // Apply the color immediately
      this.onBackgroundColorChange(hexColor);

      // Add to custom colors (maximum 1 custom color - replace if exists)
      this.addCustomColor(hexColor);
    }
    this.isColorPickerOpen = false;
  }

  // Handle color change from color picker (update preview only, don't add to list yet)
  /**
   * Normalize this.currentPickerColor to a hex string (ensuring a leading '#'), update it on the component, and apply it immediately for preview.
   * @example
   * nColorPickerChange()
   * undefined
   * @returns {void} Nothing. Updates this.currentPickerColor and calls onBackgroundColorChange for preview.
   **/
  onColorPickerChange(): void {
    if (this.currentPickerColor) {
      // Extract hex color from color object (chrome-picker returns an object with hex property)
      let hexColor: string;
      if (typeof this.currentPickerColor === 'string') {
        hexColor = this.currentPickerColor;
      } else if (
        this.currentPickerColor &&
        typeof this.currentPickerColor === 'object' &&
        'hex' in this.currentPickerColor
      ) {
        hexColor = (this.currentPickerColor as any).hex;
      } else {
        hexColor = String(this.currentPickerColor);
      }

      // Ensure hex color starts with #
      if (!hexColor.startsWith('#')) {
        hexColor = '#' + hexColor;
      }

      // Update currentPickerColor to the hex string for consistency
      this.currentPickerColor = hexColor;

      // Apply the color immediately for preview (but don't add to custom colors list yet)
      this.onBackgroundColorChange(hexColor);
    }
  }

  private addCustomColor(color: string): void {
    // Maximum 1 custom color - replace existing if any
    if (!MinimalTestimonialsPanelComponent.PREDEFINED_COLORS.includes(color)) {
      // Clear existing custom colors and add the new one
      this.customColors = [color];
    }
  }

  onCardColorChange(color: string): void {
    this.data.cardColor = color;
    this.emitChange();
  }

  onAddCardColor(): void {
    // Open color picker modal for card color
    this.openCardColorPicker();
  }

  // Open card color picker for custom color
  openCardColorPicker(color?: string): void {
    this.currentCardPickerColor = color || this.data.cardColor || '#000000';
    this.isCardColorPickerOpen = true;
  }

  // Close card color picker and add the selected color to custom colors list
  /**
   * Closes the card color picker, applies the selected color (if present) by normalizing it to a hex string (ensuring a leading '#'), adds it to the custom colors (replacing any existing custom color), and marks the picker as closed.
   * @example
   * loseCardColorPicker()
   * // returns void
   * @returns {{void}} No return value; performs side effects on the component state (applies color, updates custom colors, closes picker).
   **/
  closeCardColorPicker(): void {
    if (this.currentCardPickerColor) {
      // Extract hex color from color object (chrome-picker returns an object with hex property)
      let hexColor: string;
      if (typeof this.currentCardPickerColor === 'string') {
        hexColor = this.currentCardPickerColor;
      } else if (
        this.currentCardPickerColor &&
        typeof this.currentCardPickerColor === 'object' &&
        'hex' in this.currentCardPickerColor
      ) {
        hexColor = (this.currentCardPickerColor as any).hex;
      } else {
        hexColor = String(this.currentCardPickerColor);
      }

      // Ensure hex color starts with #
      if (!hexColor.startsWith('#')) {
        hexColor = '#' + hexColor;
      }

      // Apply the color immediately
      this.onCardColorChange(hexColor);

      // Add to custom colors (maximum 1 custom color - replace if exists)
      this.addCardCustomColor(hexColor);
    }
    this.isCardColorPickerOpen = false;
  }

  // Handle card color change from color picker (update preview only, don't add to list yet)
  /**
   * Handle changes from the card color picker: normalize the picked value to a hex string, update component state, and trigger an immediate preview without adding to custom colors.
   * @example
   * nCardColorPickerChange()
   * undefined
   * @param {void} none - No arguments.
   * @returns {void} No return value; updates currentCardPickerColor and calls onCardColorChange for preview.
   **/
  onCardColorPickerChange(): void {
    if (this.currentCardPickerColor) {
      // Extract hex color from color object (chrome-picker returns an object with hex property)
      let hexColor: string;
      if (typeof this.currentCardPickerColor === 'string') {
        hexColor = this.currentCardPickerColor;
      } else if (
        this.currentCardPickerColor &&
        typeof this.currentCardPickerColor === 'object' &&
        'hex' in this.currentCardPickerColor
      ) {
        hexColor = (this.currentCardPickerColor as any).hex;
      } else {
        hexColor = String(this.currentCardPickerColor);
      }

      // Ensure hex color starts with #
      if (!hexColor.startsWith('#')) {
        hexColor = '#' + hexColor;
      }

      // Update currentCardPickerColor to the hex string for consistency
      this.currentCardPickerColor = hexColor;

      // Apply the color immediately for preview (but don't add to custom colors list yet)
      this.onCardColorChange(hexColor);
    }
  }

  private addCardCustomColor(color: string): void {
    // Maximum 1 custom color - replace existing if any
    if (!MinimalTestimonialsPanelComponent.PREDEFINED_COLORS.includes(color)) {
      // Clear existing custom colors and add the new one
      this.cardCustomColors = [color];
    }
  }

  private emitChange(): void {
    this.dataChange.emit({ ...this.data });
  }

  getTestimonialLabel(index: number): string {
    return `Testimonial ${index + 1}`;
  }

  onImageReplaceClick(index: number): void {
    const fileInput = document.getElementById(`testimonial-image-${index}`) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  /**
   * Handle image file selection for a testimonial avatar: validates file size and type, then uploads valid images.
   * @example
   * nImageFileSelected(0, event)
   * undefined
   * @param {{number}} {{index}} - Index of the image slot being updated.
   * @param {{Event}} {{event}} - The input change event containing the selected File.
   * @returns {{void}} No return value; uploads the valid image via handleAvatarUpload or shows an alert on validation failure.
   **/
  onImageFileSelected(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        this.messageService.add({
          severity: 'warn',
          summary: 'File Too Large',
          detail: 'File size must be less than 5MB',
        });
        return;
      }

      // Validate file type
      if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Invalid File Type',
          detail: 'Only PNG, JPG, and JPEG files are allowed',
        });
        return;
      }

      // Upload file to server
      this.handleAvatarUpload(index, file);
    }
  }

  /**
   * Uploads an avatar file for a testimonial: sets a temporary preview, performs the server upload, replaces the preview with the uploaded URL on success, and updates component state and errors accordingly.
   * @example
   * handleAvatarUpload(0, someFile)
   * undefined
   * @param {{number}} {{index}} - Index of the testimonial whose avatar is being uploaded.
   * @param {{File}} {{file}} - File object representing the avatar image to upload.
   * @returns {{void}} Returns nothing; performs state updates, emits changes, and handles upload success/error.
   **/
  private handleAvatarUpload(index: number, file: File): void {
    // Show loading state
    this.uploadingAvatars[index] = true;
    this.uploadErrors[index] = null;

    // Create temporary preview URL for immediate feedback
    const tempPreviewUrl = URL.createObjectURL(file);
    if (this.data.testimonials[index]) {
      this.data.testimonials[index].avatar = tempPreviewUrl;
      this.emitChange();
    }

    // Upload to server
    this.templateService.uploadWebsiteFile(file).subscribe({
      next: response => {
        // Extract image URL from response
        let imageUrl = '';
        if (typeof response === 'string') {
          imageUrl = response;
        } else if (response?.url) {
          imageUrl = response.url;
        } else if (response?.data?.url) {
          imageUrl = response.data.url;
        } else if (response?.imageUrl) {
          imageUrl = response.imageUrl;
        } else if (response?.fileUrl) {
          imageUrl = response.fileUrl;
        }

        if (imageUrl) {
          // Replace temporary URL with uploaded URL
          URL.revokeObjectURL(tempPreviewUrl);
          if (this.data.testimonials[index]) {
            this.data.testimonials[index].avatar = imageUrl;
            this.emitChange();
          }
        } else {
          this.logger.error('No image URL in response:', response);
          this.uploadErrors[index] = 'Failed to get image URL from server';
        }

        this.uploadingAvatars[index] = false;
      },
      error: error => {
        this.logger.error('Error uploading avatar:', error);
        this.uploadErrors[index] = 'Failed to upload image. Please try again.';
        this.uploadingAvatars[index] = false;
        // Keep the temporary preview on error
      },
    });
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
